import React, { useState, useEffect } from 'react';
import axios from 'axios';

const FeaturedServicesModal = ({ isOpen, onClose, initialData }) => {
  const [servicesData, setServicesData] = useState({
    services: []
  });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [activeServiceIndex, setActiveServiceIndex] = useState(0);

  // Fetch current data when modal opens
  useEffect(() => {
    const fetchCurrentData = async () => {
      if (isOpen && !initialData) {
        try {
          setIsLoading(true);
          const response = await fetch(`${import.meta.env.VITE_API_URL}/services/featured-services/`);
          if (response.ok) {
            const data = await response.json();
            setServicesData(data);
          }
        } catch (error) {
          console.error('Error fetching featured services data:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchCurrentData();
  }, [isOpen, initialData]);

  // If initialData is provided, use that instead of fetching
  useEffect(() => {
    if (initialData) {
      setServicesData(initialData);
    }
  }, [initialData]);

  const handleChange = (index, field, value) => {
    const updatedServices = [...servicesData.services];
    updatedServices[index] = { ...updatedServices[index], [field]: value };
    setServicesData(prev => ({ ...prev, services: updatedServices }));
  };

  const handleDescriptionChange = (index, descIndex, value) => {
    const updatedServices = [...servicesData.services];
    const updatedDescription = [...updatedServices[index].description];
    updatedDescription[descIndex] = value;
    updatedServices[index] = { 
      ...updatedServices[index], 
      description: updatedDescription 
    };
    setServicesData(prev => ({ ...prev, services: updatedServices }));
  };

  const handleImageUpload = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);
    formData.append('category', 'service_images');

    try {
      setIsUploading(true);
      setUploadProgress(0);

      const response = await axios.post(`${import.meta.env.VITE_API_URL}/images/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        }
      });

      // Update the image with the URL from the response
      handleChange(index, 'image', response.data.image || response.data.image.url);
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const addNewService = () => {
    const newService = {
      id: servicesData.services.length + 1,
      title: "New Service",
      description: ["Description paragraph 1", "Description paragraph 2"],
      buttonText: "Learn More",
      buttonColor: "#0760ad",
      image: "",
      bgColor: "bg-gray-50"
    };
    setServicesData(prev => ({
      ...prev,
      services: [...prev.services, newService]
    }));
    setActiveServiceIndex(servicesData.services.length);
  };

  const removeService = (index) => {
    const updatedServices = servicesData.services.filter((_, i) => i !== index);
    setServicesData(prev => ({ ...prev, services: updatedServices }));
    if (activeServiceIndex >= updatedServices.length) {
      setActiveServiceIndex(updatedServices.length - 1);
    }
  };

  const handleSubmit = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/services/featured-services/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(servicesData)
      });

      if (response.ok) {
        const updatedData = await response.json();
        onClose();
        window.location.reload(); // Only reload if needed
      } else {
        console.error('Failed to update featured services data');
        alert('Failed to save changes. Please try again.');
      }
    } catch (error) {
      console.error('Error updating featured services data:', error);
      alert('An error occurred while saving. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <dialog className={`modal ${isOpen ? 'modal-open' : ''}`}>
        <div className="modal-box">
          <span className="loading loading-spinner loading-lg"></span>
          <p>Loading featured services data...</p>
        </div>
      </dialog>
    );
  }

  return (
    <dialog className={`modal ${isOpen ? 'modal-open' : ''}`}>
      <div className="modal-box w-11/12 max-w-5xl max-h-[90vh] relative">
        <button onClick={onClose} className="btn btn-sm btn-circle absolute right-2 top-2">✕</button>
        <h3 className="font-bold text-lg mb-4">Edit Featured Services</h3>
        
        {/* Service Navigation */}
        <div className="tabs tabs-boxed mb-6">
          {servicesData.services?.map((service, index) => (
            <button
              key={index}
              className={`tab ${activeServiceIndex === index ? 'tab-active' : ''}`}
              onClick={() => setActiveServiceIndex(index)}
            >
              Service {index + 1}
            </button>
          ))}
          <button 
            className="tab"
            onClick={addNewService}
          >
            + Add Service
          </button>
        </div>
        
        <div className="overflow-y-auto max-h-[60vh] pr-2">
          {servicesData.services?.length > 0 ? (
            <div className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <h4 className="font-bold text-lg">Editing Service {activeServiceIndex + 1}</h4>
                {servicesData.services.length > 1 && (
                  <button 
                    className="btn btn-sm btn-error"
                    onClick={() => removeService(activeServiceIndex)}
                  >
                    Remove Service
                  </button>
                )}
              </div>
              
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Title</span>
                </label>
                <input 
                  type="text" 
                  value={servicesData.services[activeServiceIndex]?.title || ''} 
                  onChange={(e) => handleChange(activeServiceIndex, 'title', e.target.value)} 
                  className="input input-bordered w-full" 
                />
              </div>
              
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Background Color Class</span>
                </label>
                <select
                  value={servicesData.services[activeServiceIndex]?.bgColor || 'bg-gray-50'} 
                  onChange={(e) => handleChange(activeServiceIndex, 'bgColor', e.target.value)} 
                  className="select select-bordered w-full"
                >
                  <option value="bg-gray-50">Light Gray</option>
                  <option value="bg-white">White</option>
                  <option value="bg-blue-50">Light Blue</option>
                  <option value="bg-green-50">Light Green</option>
                </select>
              </div>
              
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Button Text</span>
                </label>
                <input 
                  type="text" 
                  value={servicesData.services[activeServiceIndex]?.buttonText || ''} 
                  onChange={(e) => handleChange(activeServiceIndex, 'buttonText', e.target.value)} 
                  className="input input-bordered w-full" 
                />
              </div>
              
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Button Color</span>
                </label>
                <input 
                  type="color" 
                  value={servicesData.services[activeServiceIndex]?.buttonColor || '#0760ad'} 
                  onChange={(e) => handleChange(activeServiceIndex, 'buttonColor', e.target.value)} 
                  className="input input-bordered w-20 h-10 p-1" 
                />
              </div>
              
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Service Image</span>
                </label>
                <div className="flex items-center gap-4">
                  <input 
                    type="file" 
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, activeServiceIndex)}
                    className="file-input file-input-bordered w-full max-w-xs"
                    disabled={isUploading}
                  />
                  {isUploading && (
                    <progress 
                      className="progress progress-primary w-56" 
                      value={uploadProgress} 
                      max="100"
                    ></progress>
                  )}
                </div>
                {servicesData.services[activeServiceIndex]?.image && (
                  <div className="mt-2">
                    <div className="text-sm text-gray-500 mb-1">Current Image:</div>
                    <img 
                      src={servicesData.services[activeServiceIndex].image} 
                      alt="Service preview" 
                      className="max-h-40 rounded-md border"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://via.placeholder.com/800x400?text=Image+Not+Found';
                      }}
                    />
                  </div>
                )}
              </div>
              
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Description Paragraphs</span>
                </label>
                {servicesData.services[activeServiceIndex]?.description?.map((paragraph, index) => (
                  <textarea
                    key={index}
                    value={paragraph}
                    onChange={(e) => handleDescriptionChange(activeServiceIndex, index, e.target.value)}
                    className="textarea textarea-bordered w-full mb-2"
                    rows="3"
                  />
                ))}
                <button 
                  className="btn btn-sm mt-2"
                  onClick={() => {
                    const updatedServices = [...servicesData.services];
                    updatedServices[activeServiceIndex].description.push("");
                    setServicesData(prev => ({ ...prev, services: updatedServices }));
                  }}
                >
                  Add Paragraph
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p>No services found. Add your first service!</p>
              <button className="btn btn-primary mt-4" onClick={addNewService}>
                Add Service
              </button>
            </div>
          )}
        </div>
        
        <div className="modal-action mt-4">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit}>
            Save Changes
          </button>
        </div>
      </div>
    </dialog>
  );
};

export default FeaturedServicesModal;